import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import axiosInstance, { getErrorMessage } from 'utils/axios';
import CRUD from 'utils/Crud';
import {
  Card,
  Button,
  Stack,
  Typography,
  CircularProgress,
  Box,
  Alert,
  AlertTitle,
  useTheme,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from 'components/Iconify'; // Assuming you have an Iconify component for icons

AuditContainer.propTypes = {
  user: PropTypes.object.isRequired,
};

// Map status to visual properties
const AUDIT_STATUS_MAP = {
  success: {
    icon: 'eva:checkmark-circle-2-fill',
    color: 'success.main',
    title: 'Audit Successful!',
  },
  failed: {
    icon: 'eva:alert-triangle-fill',
    color: 'error.main',
    title: 'Audit Failed!',
  },
};

export default function AuditContainer({ user }) {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const [auditState, setAuditState] = useState({
    loading: false,
    error: null, // API error message
    auditResult: null, // { status: 'success'/'failed', message: '...' } from server
  });

  const handleRequestAudit = async () => {
    setAuditState({ ...auditState, loading: true, error: null, auditResult: null });
    try {
      const response = await axiosInstance.get(`${CRUD.user.auditBalance}?user_id=${user.user_id}`, {
        user_id: user.user_id,
      });

      // Assuming server returns { status: 'success' | 'failed', message: 'Some message' }
      const { status, message } = response.data;

      if (status === true) {
        setAuditState({
          ...auditState,
          loading: false,
          auditResult: { status: 'success', message },
        });
        // enqueueSnackbar('Audit completed successfully!', { variant: 'success' });
      } else {
        // Server returned 'failed' status
        setAuditState({
          ...auditState,
          loading: false,
          auditResult: { status: 'failed', message: message || 'Audit failed due to an unknown reason.' },
        });
        // enqueueSnackbar(message || 'Audit failed.', { variant: 'warning' });
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setAuditState({
        ...auditState,
        loading: false,
        error: errorMessage,
        auditResult: null,
      });
      enqueueSnackbar(errorMessage, { variant: 'error' }); // Snackbar for API request errors
    }
  };

  const handleRetryAudit = () => {
    setAuditState({ ...auditState, error: null, auditResult: null }); // Clear previous results/errors
    handleRequestAudit(); // Retry the audit
  };

  const renderAuditResult = () => {
    if (!auditState.auditResult) return null;

    const { status, message } = auditState.auditResult;
    const { icon, color, title } = AUDIT_STATUS_MAP[status];

    return (
      <Alert
        severity={status === 'success' ? 'success' : 'error'}
        variant="filled"
        sx={{ mt: 3, mb: 2, display: 'flex', alignItems: 'center' }}
      >
        {/* <Iconify icon={icon} sx={{ width: 24, height: 24, mr: 1, color: 'common.white' }} /> */}
        <Box>
          <AlertTitle sx={{ color: 'common.white', fontWeight: 'bold' }}>{title}</AlertTitle>
          <Typography variant="body2" sx={{ color: 'common.white' }}>
            {message}
          </Typography>
        </Box>
      </Alert>
    );
  };

  return (
    <Card sx={{ p: 3, mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        Balance Audit
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Request a real-time audit of the user's balance for verification purposes.
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Stack spacing={2} alignItems="flex-start">
        {!auditState.loading &&
        <LoadingButton
          variant="outlined"
          onClick={handleRequestAudit}
          loading={auditState.loading}
          startIcon={auditState.loading ? null : <Iconify icon="eva:refresh-fill" />}
          loadingPosition="start"
        >
          {auditState.loading ? 'Auditing...' : 'Request Balance Audit'}
        </LoadingButton>}

        {auditState.loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Processing audit request...
            </Typography>
          </Stack>
        )}

        {/* Display audit result from server */}
        {renderAuditResult()}

        {/* Display general API request error if any */}
        {auditState.error && (
          <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
            <AlertTitle>Request Error</AlertTitle>
            {auditState.error}
            <Button onClick={handleRetryAudit} size="small" sx={{ mt: 1 }}>
              Retry Audit
            </Button>
          </Alert>
        )}
      </Stack>
    </Card>
  );
}
